import { type FunctionComponent, useEffect, useState } from 'react'

import { type AdvisorDetails } from '../../../../types/azure'

import { showAlert } from 'src/actions/alertsActions'
import { startLoading, stopLoading } from 'src/actions/loadingActions'

import api from 'src/constants/api'

import Banner from 'src/components/Common/Banner'
import Section from 'src/components/Common/Section'
import Spacer from 'src/components/Common/Spacer'
import TenantDropdown from 'src/components/Common/TenantDropdown'

import axios, { type AxiosResponse } from 'axios'
import { FcHighPriority, FcLowPriority, FcMediumPriority } from 'react-icons/fc'
import { PiChartLine, PiGear, PiGlobe, PiMoney, PiShield } from 'react-icons/pi'
import { type RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

const advisorCategories = [
  {
    category: 'Cost',
    displayName: 'Cost',
    icon: <PiMoney />,
    details: {
      annualSavingsAmount: 0,
      highCount: 0,
      impactedResourcesCount: 0,
      lowCount: 0,
      mediumCount: 0,
      totalCount: 0
    },
    recommendations: 0
  },
  {
    category: 'Security',
    displayName: 'Security',
    icon: <PiShield />,
    details: {
      annualSavingsAmount: 0,
      highCount: 0,
      impactedResourcesCount: 0,
      lowCount: 0,
      mediumCount: 0,
      totalCount: 0
    },
    recommendations: 0
  },
  {
    category: 'HighAvailability',
    displayName: 'Reliability',
    icon: <PiGlobe />,
    details: {
      annualSavingsAmount: 0,
      highCount: 0,
      impactedResourcesCount: 0,
      lowCount: 0,
      mediumCount: 0,
      totalCount: 0
    },
    recommendations: 0
  },
  {
    category: 'OperationalExcellence',
    displayName: 'Operational Excellence',
    icon: <PiGear />,
    details: {
      annualSavingsAmount: 0,
      highCount: 0,
      impactedResourcesCount: 0,
      lowCount: 0,
      mediumCount: 0,
      totalCount: 0
    },
    recommendations: 0
  },
  {
    category: 'Performance',
    displayName: 'Performance',
    icon: <PiChartLine />,
    details: {
      annualSavingsAmount: 0,
      highCount: 0,
      impactedResourcesCount: 0,
      lowCount: 0,
      mediumCount: 0,
      totalCount: 0
    },
    recommendations: 0
  }
]

const AzureAdvisor: FunctionComponent = (): JSX.Element => {
  const account = useSelector((state: RootStateOrAny) => state.account)
  const tenant = useSelector((state: RootStateOrAny) => state.tenant)

  const dispatch = useDispatch()

  const [advisorDetails, setAdvisorDetails] = useState(advisorCategories)

  useEffect(() => {
    const source = axios.CancelToken.source()

    dispatch(startLoading())

    if (tenant.id !== '')
      api
        .get(`/soteria/account/${account.id}/tenant/${tenant.id}/azure_advisor`, { cancelToken: source.token })
        .then((response: AxiosResponse) => {
          if (response.data) {
            const d: AdvisorDetails = response.data.azureAdvisorDetails

            const advisorDetails = advisorCategories.map((c) => {
              const category: string = c.category
              const index = category as keyof typeof d.categoryDetails

              return {
                ...c,
                details: d.categoryDetails[index] || c.details,
                recommendations: d.categoryDetails[index]
                  ? d.categoryDetails[index].highCount + d.categoryDetails[index].mediumCount + d.categoryDetails[index].lowCount
                  : 0
              }
            })

            setAdvisorDetails(advisorDetails)
            dispatch(stopLoading())
          }
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            dispatch(
              showAlert({
                type: 'error',
                message: 'We were unable to retrieve your secure score data.',
                component: 'Security',
                error
              })
            )
          }
        })
    return () => {
      source.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant])

  return (
    <Section>
      <Banner>
        <Spacer />
        <TenantDropdown />
      </Banner>
      <Spacer />
      Azure Advisor analyses your configurations and usage telemetry and offers personalised, actionable recommendations to help you optimise your Azure
      resources for reliability, security, operational excellence, performance, and cost.
      <Spacer />
      <div className="flex flex-wrap gap-8">
        {advisorDetails.map((category) => (
          <Link
            className="w-max overflow-hidden rounded-lg border border-th-border duration-100 hover:shadow-md"
            to={`/azure_advisor/recommendations?category=${category.category}`}
          >
            <div className="flex h-16 w-full bg-th-content-secondary px-4 text-xl">
              <div className="flex h-full items-center gap-4">
                {category.icon}
                <div>{category.displayName}</div>
              </div>
              <Spacer />
            </div>
            <div className="border-b border-th-border p-4">
              <div className="flex items-center gap-4">
                <div className="text-2xl font-bold">{category.recommendations}</div>
                <span>Recommendations</span>
              </div>
              <div className="my-4 flex h-4 w-full overflow-hidden rounded bg-th-border">
                <div
                  style={{
                    width: (category.details.highCount / category.recommendations) * 100 + '%'
                  }}
                  className="bg-th-error"
                ></div>
                <div
                  style={{
                    width: (category.details.mediumCount / category.recommendations) * 100 + '%'
                  }}
                  className="bg-th-warning"
                ></div>
                <div
                  style={{
                    width: (category.details.lowCount / category.recommendations) * 100 + '%'
                  }}
                  className="bg-th-success"
                ></div>
              </div>
              <div className="flex items-center gap-4">
                <Link
                  className="flex w-24 items-center gap-4 rounded-lg p-2 duration-100 hover:bg-th-content-secondary"
                  to={`/azure_advisor/recommendations?impact=high&category=${category.category}`}
                >
                  <FcHighPriority className="h-8 w-8" />
                  <div className="text-xl font-bold">{category.details.highCount}</div>
                </Link>
                <Link
                  className="flex w-24 items-center gap-4 rounded-lg p-2 duration-100 hover:bg-th-content-secondary"
                  to={`/azure_advisor/recommendations?impact=medium&category=${category.category}`}
                >
                  <FcMediumPriority className="h-8 w-8" />
                  <div className="text-xl font-bold">{category.details.mediumCount}</div>
                </Link>
                <Link
                  className="flex w-24 items-center gap-4 rounded-lg p-2 duration-100 hover:bg-th-content-secondary"
                  to={`/azure_advisor/recommendations?impact=low&category=${category.category}`}
                >
                  <FcLowPriority className="h-8 w-8" />
                  <div className="text-xl font-bold">{category.details.lowCount}</div>
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4">
              <div className="text-2xl font-bold">{category.details.impactedResourcesCount}</div>
              <span>Impacted Resources</span>
            </div>
          </Link>
        ))}
      </div>
    </Section>
  )
}

export default AzureAdvisor
